import {
  type MarketCategory,
  type MarketCategoryEntry,
  useBettingMarketCategories,
} from "./useBettingMarketCategories";
import type { EventMarket } from "./useBetting";
import { isMatch } from "lodash";
import { useMemo } from "react";
import { sortByPriority } from "utilities/sortMarkets";
import groupMarketsFallback from "utilities/groupMarkets";
import { shouldShowPromoBanner } from "utilities/sharedBettingUtilities";
import { type GroupedTokens, useTokens } from "../v2/user/useTokens";
import type { Permissions } from "utilities/Auth/authSlice";
import { useCampaigns } from "../v2/user/useCampaigns";
import { parseBool } from "utilities/helpers";
import type { Maybe } from "types/utilities";

const ADDITIONAL_MARKETS_LABEL = "Additional Markets";
const additionalMarketsCategory = {
  name: ADDITIONAL_MARKETS_LABEL,
  priority: Number.MAX_VALUE,
};
const getMatchingCategoryEntry = (
  market: EventMarket,
  category: MarketCategory,
): Maybe<MarketCategoryEntry> => {
  return category.entries?.find((entry) => {
    if (entry.marketType != "*" && entry.marketType != market.marketType) {
      return false;
    } else {
      return isMatch(market.attributes || {}, entry.attributes || {});
    }
  });
};

export type GroupedMarkets = {
  priority: number;
  name: string;
  markets: EventMarket[];
  hasPromotionalMarkets: boolean;
};

/**
 * Group markets into the matching categories.
 *
 * Exported for testing purposes only. It is a pure function so no mocking of React hooks is required. Attempted to mock react but wasn't trivial.
 */
export function groupMarkets(
  markets: EventMarket[],
  categories: MarketCategory[],
  sport: string,
  useOldGrouping = false,
  userCampaigns: string[] = [],
  userTokens: GroupedTokens = {},
  permissions: Permissions = {},
) {
  if (
    !categories ||
    categories.length === 0 ||
    (parseBool(process.env.GATSBY_USE_LEGACY_MARKET_CATEGORIES, true) &&
      useOldGrouping)
  ) {
    return Object.entries(groupMarketsFallback(markets, sport)).map(
      ([categoryName, markets], i) => {
        const hasPromotionalMarkets =
          permissions["viewPromotion"] === "GRANTED" &&
          markets.some(
            (market) =>
              market.hasPromotionalMarkets ||
              market.promotionCampaigns?.some((campaign) =>
                shouldShowPromoBanner(
                  campaign,
                  userCampaigns,
                  userTokens,
                  permissions,
                ),
              ),
          );

        return {
          name: categoryName,
          markets: markets.sort(sortByPriority),
          priority: i,
          hasPromotionalMarkets,
        };
      },
    ) as GroupedMarkets[];
  } else {
    const additionalMarkets = new Set(markets);
    const customCategoryMarketsMap = new Map<string, GroupedMarkets>();
    const categoryMarketsMap = new Map<string, GroupedMarkets>();

    // First pass: assign markets to categories or customCategoryMarkets
    markets.forEach((market, i) => {
      if (market.category) {
        additionalMarkets.delete(market);

        const currentGroup = customCategoryMarketsMap.get(market.category) || {
          name: market.category,
          priority: i,
          markets: [],
          hasPromotionalMarkets: false,
        };

        currentGroup.markets.push(market);
        currentGroup.markets.sort(sortByPriority);
        customCategoryMarketsMap.set(market.category, currentGroup);
      } else {
        categories.forEach((category) => {
          const entry = getMatchingCategoryEntry(market, category);

          if (entry) {
            additionalMarkets.delete(market);
            const currentCategory = categoryMarketsMap.get(category.name) || {
              ...category,
              markets: [],
              hasPromotionalMarkets: false,
            };

            currentCategory.markets.push({
              ...market,
              priority: entry.priority,
            });
            currentCategory.markets.sort(sortByPriority);
            categoryMarketsMap.set(category.name, currentCategory);
          }
        });
      }
    });

    // Second pass: check if any of the markets have a promotion available.
    categoryMarketsMap.forEach((category) => {
      category.hasPromotionalMarkets = category.markets.some(
        (market) => market.hasPromotionalMarkets,
      );
    });

    const customCategoryMarkets = Array.from(customCategoryMarketsMap.values());
    const categoryMarkets = Array.from(categoryMarketsMap.values());

    if (customCategoryMarkets.length > 0) {
      categoryMarkets.push(...customCategoryMarkets);
    }

    if (additionalMarkets.size > 0) {
      const additionalMarketsSorted =
        Array.from(additionalMarkets).sort(sortByPriority);
      categoryMarkets.push({
        ...additionalMarketsCategory,
        markets: additionalMarketsSorted,
        hasPromotionalMarkets: additionalMarketsSorted.some(
          (market) => market.hasPromotionalMarkets,
        ),
      });
    }

    return categoryMarkets.sort(sortByPriority);
  }
}

export const useGroupedMarkets = (
  markets: EventMarket[],
  sport: string,
  useOldGrouping?: boolean,
): [GroupedMarkets[], boolean] => {
  const [categories, loading] = useBettingMarketCategories(sport);
  const { codes: userCampaigns } = useCampaigns();
  const { tokens: userTokens } = useTokens();

  const groupedMarkets = useMemo(() => {
    if (loading) {
      return [];
    }

    return groupMarkets(
      markets,
      categories,
      sport,
      useOldGrouping,
      userCampaigns,
      userTokens,
    );
  }, [markets, categories, loading]);

  return [groupedMarkets, loading];
};
